import React, { useEffect, useState } from "react";

import {
  Box,
  CssBaseline,
  Divider,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import { styled, Theme, useTheme } from "@mui/material/styles";
import { Outlet, useLocation } from "react-router-dom";

import {
  APP_BOLO_ALERT,
  APP_CANVAS,
  APP_CONNECT,
  APP_FEEDBACK,
  APP_INTEL,
  APP_MESSAGE,
  APP_PARTNER,
  APP_REGISTRY,
  APP_REPORT_V2,
  APP_USERS_ADMIN,
  NO_APP,
} from "@/common/constants";
import { useSidebar } from "@/contexts/SidebarContext";

import CustomAppBar, { APP_BAR_HEIGHT } from "./AppBar";
import AppsSideBar from "./menu/AppsSideBar";
import { Copyright } from "./menu/copyright";
import RubiBoloMenu from "./menu/RubiBoloMenu";
import RubiCanvasMenu from "./menu/RubiCanvasMenu";
import RubiConnectMenu from "./menu/RubiConnectMenu";
import RubiFeedbackMenu from "./menu/RubiFeedbackMenu";
import RubiIntelMenu from "./menu/RubiIntelMenu";
import RubiMessageMenu from "./menu/RubiMessageMenu";
import RubiNoMenu from "./menu/RubiNoMenu";
import RubiPartnerMenu from "./menu/RubiPartnerMenu";
import RubiRegistryMenu from "./menu/RubiRegistryMenu";
import RubiReportV2Menu from "./menu/RubiReportV2Menu";
import UsersAdminMenu from "./menu/UsersAdminMenu";

const FULL_DRAWER_WIDTH = 240;
const ICON_COLUMN_WIDTH = 56;
const SUBMENU_WIDTH = FULL_DRAWER_WIDTH - ICON_COLUMN_WIDTH - 1;

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "collapsed",
})<{
  open?: boolean;
  collapsed?: boolean;
}>(({ theme, open, collapsed }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflow: "hidden",
  position: "relative",
  display: "flex",
  flexDirection: "column",
}));

const MainLayout: React.FC = () => {
  const theme = useTheme<Theme>();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const location = useLocation();

  const { selectedApp, setSelectedApp, collapsed, setCollapsed } = useSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const effectiveOpen = isSmUp ? true : mobileOpen;

  useEffect(() => {
    const sidebarWidth = !isSmUp
      ? 0
      : collapsed
        ? ICON_COLUMN_WIDTH
        : FULL_DRAWER_WIDTH;
    document.documentElement.style.setProperty(
      "--sidebar-width",
      `${sidebarWidth}px`,
    );
  }, [collapsed, isSmUp]);

  useEffect(() => {
    const top = location.pathname.split("/")[1];
    switch (top) {
      case "rubialert":
        setSelectedApp(APP_BOLO_ALERT);
        break;
      case "organizations":
        setSelectedApp(APP_USERS_ADMIN);
        break;
      case "rubireportv2":
        setSelectedApp(APP_REPORT_V2);
        break;
      case "rubiintel":
        setSelectedApp(APP_INTEL);
        break;
      case "rubicanvas":
        setSelectedApp(APP_CANVAS);
        break;
      case "rubiregistry":
        setSelectedApp(APP_REGISTRY);
        break;
      case "rubimessage":
        setSelectedApp(APP_MESSAGE);
        break;
      case "rubiconnect":
        setSelectedApp(APP_CONNECT);
        break;
      case "rubifeedback":
        setSelectedApp(APP_FEEDBACK);
        break;
      case "rubipartner":
        setSelectedApp(APP_PARTNER);
        break;
      default:
        setSelectedApp(NO_APP);
    }
  }, [location.pathname, setSelectedApp]);

  const handleToggleSidebar = () => {
    if (isSmUp) {
      setCollapsed(!collapsed);
    } else {
      setMobileOpen((prev) => !prev);
    }
  };

  const SubMenu =
    (
      {
        [APP_BOLO_ALERT]: RubiBoloMenu,
        [APP_USERS_ADMIN]: UsersAdminMenu,
        [APP_REPORT_V2]: RubiReportV2Menu,
        [APP_INTEL]: RubiIntelMenu,
        [APP_CANVAS]: RubiCanvasMenu,
        [APP_REGISTRY]: RubiRegistryMenu,
        [APP_MESSAGE]: RubiMessageMenu,
        [APP_CONNECT]: RubiConnectMenu,
        [APP_FEEDBACK]: RubiFeedbackMenu,
        [APP_PARTNER]: RubiPartnerMenu,
      } as Record<string, React.ComponentType<any>>
    )[selectedApp] || RubiNoMenu;

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Divider sx={{ borderColor: "frame.border" }} />
      <Box sx={{ display: "flex", flexGrow: 1, width: "100%" }}>
        {!collapsed || selectedApp === NO_APP ? (
          <Box
            sx={{
              width: ICON_COLUMN_WIDTH,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 1,
            }}
          >
            <AppsSideBar />
          </Box>
        ) : (
          <Box
            sx={{
              width: ICON_COLUMN_WIDTH,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 1,
            }}
          >
            <SubMenu collapsed={true} />
          </Box>
        )}

        {!collapsed && (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "frame.border" }}
            />
            <Box
              sx={{
                width: SUBMENU_WIDTH,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
              }}
            >
              <Box sx={{ flexGrow: 1, pt: 1 }}>
                <SubMenu collapsed={false} />
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Divider sx={{ borderColor: "frame.border" }} />
      <Copyright />
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      <CssBaseline enableColorScheme />
      <CustomAppBar onMenuClick={handleToggleSidebar} />

      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          mt: `${APP_BAR_HEIGHT}px`,
          height: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
        }}
      >
        <Box
          component="nav"
          sx={{
            width: { sm: collapsed ? ICON_COLUMN_WIDTH : FULL_DRAWER_WIDTH },
            flexShrink: { sm: 0 },
          }}
          aria-label="apps & submenu sidebar"
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleToggleSidebar}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                top: `${APP_BAR_HEIGHT}px`,
                height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
                width: FULL_DRAWER_WIDTH,
                backgroundColor: "frame.bg",
                // No dark-mode elevation overlay, so it matches the app bar.
                backgroundImage: "none",
                color: "frame.text.primary",
              },
            }}
          >
            {drawerContent}
          </Drawer>

          <Drawer
            variant="permanent"
            open
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                top: `${APP_BAR_HEIGHT}px`,
                height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
                width: collapsed ? ICON_COLUMN_WIDTH : FULL_DRAWER_WIDTH,
                backgroundColor: "frame.bg",
                backgroundImage: "none",
                color: "frame.text.primary",
                transition: "width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                overflowX: "hidden",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>

        <Main open={effectiveOpen} collapsed={collapsed}>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <Outlet />
          </Box>
        </Main>
      </Box>
    </Box>
  );
};

export default MainLayout;
